import { z } from 'zod';
import { text } from './models/common.mjs';

export const field = text(80)
  .trim()
  .min(1)
  .refine((s) => !['__proto__', 'constructor', 'prototype'].includes(s));
const cell = z.union([z.string().max(4000), z.number().finite(), z.boolean(), z.null()]);
// A saved definition may precede its first snapshot. Empty data means no
// observed rows, never a fabricated zero or placeholder observation.
export const rows = z.array(z.record(field, cell)).max(1000);

export const currency = z.string().regex(/^[A-Z]{3}$/);

export function validateRows(keys, rows, ctx) {
  if (
    new Set(keys).size !== keys.length ||
    rows.some(
      (row) =>
        Object.keys(row).some((key) => !keys.includes(key)) ||
        keys.some((key) => !Object.hasOwn(row, key)),
    )
  )
    ctx.addIssue({ code: 'custom', message: 'Rows must match unique declared columns' });
}

export const chartTable = (chart) => ({
  columns: [chart.xKey, ...chart.yKeys].map((key) =>
    chart.currency && key !== chart.xKey
      ? { key, label: key, type: /** @type {const} */ ('money'), currency: chart.currency }
      : { key, label: key },
  ),
  rows: chart.rows,
});

export function csvText(table) {
  const escape = (value) => {
    const text = String(value ?? '');
    // Normalize only for classification; retain the original cell bytes.
    const protect =
      !(typeof value === 'number' && Number.isFinite(value)) &&
      /^[\s\p{Cc}\p{Cf}]*[=+@\-\t\r]/u.test(text.normalize('NFKC'));
    return '"' + (protect ? "'" : '') + text.replaceAll('"', '""') + '"';
  };
  return [
    table.columns.map((column) => column.label),
    ...table.rows.map((row) => table.columns.map((column) => row[column.key] ?? null)),
  ]
    .map((row) => row.map(escape).join(','))
    .join('\r\n');
}

export function tableChart(table) {
  const numeric = table.columns.filter(
    (column) =>
      table.rows.length > 0 &&
      table.rows.every(
        (row) => typeof row[column.key] === 'number' && Number.isFinite(row[column.key]),
      ),
  );
  const x = table.columns.find((column) => !numeric.includes(column)) ?? table.columns[0];
  const yKeys = numeric.filter((column) => column.key !== x?.key).map((column) => column.key);
  return yKeys.length && x
    ? {
        type: /** @type {const} */ ('bar'),
        xKey: x.key,
        yKeys,
        rows: table.rows,
      }
    : null;
}
